import { useMemo } from 'react';

/**
 * Kaharian v3.0 — Mandatory Idempotency-Key for every paid AI "Generate" button.
 *
 * Returns a STABLE UUID v4 keyed to the user's intent (via useMemo deps).
 *   • Survives React StrictMode double-renders
 *   • Survives fast double-clicks (same deps → same key → Hub returns original task, no double charge)
 *   • Invalidates only when the user actually changes their inputs
 *
 * Usage:
 *   const idempotencyKey = useIdempotencyKey('konnect-music', [prompt, customLyrics, instrumental, vocalType]);
 *   await axios.post('/api/creative-studio/generate/music', { ..., idempotency_key: idempotencyKey });
 *
 * Falls back to Math.random-based UUID in environments without crypto.randomUUID (older Safari).
 */
function uuidv4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useIdempotencyKey(prefix, deps = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => `${prefix}-${uuidv4()}`, deps);
}
